/**
 * IndexDetailModal — chart view for an index with period selectors.
 */

import React, { useState, useEffect, useRef } from 'react';
import { getIndexDetail } from '../../services/fundsService';
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    ReferenceLine,
} from 'recharts';
import '../../styles/FundsPage.css';

const PERIODS = ['1D', '5D', '1M', '3M', '6M', '1Y', '3Y', '5Y'];
const unwrap = (res) => (res && typeof res === 'object' && 'data' in res ? res.data : res);
const formatMetric = (value) => (value == null ? '—' : Number(value).toFixed(2));
const CHART_OPTIONS = [
    { key: 'close', label: 'Nifty Points' },
    { key: 'pe', label: 'PE' },
    { key: 'eps', label: 'EPS' },
    { key: 'pb', label: 'P/B' },
];

const IndexDetailModal = ({ index, onClose }) => {
    const [period, setPeriod] = useState('5Y');
    const [chartData, setChartData] = useState([]);
    const [valuationData, setValuationData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [valuationLoading, setValuationLoading] = useState(false);
    const [meta, setMeta] = useState(null);
    const [selectedMetric, setSelectedMetric] = useState('close');
    const requestSeqRef = useRef(0);
    // Cache fetched valuation data per (symbol, period) to avoid re-fetching
    const valuationCacheRef = useRef({});

    // Primary fetch: close-only for curated indices (fast path),
    // but archive indices always include valuation for free.
    useEffect(() => {
        if (!index?.symbol) return;

        const controller = new AbortController();
        const seq = ++requestSeqRef.current;
        setLoading(true);
        setValuationData([]);

        getIndexDetail(index.symbol, period, index.name, {
            signal: controller.signal,
            includeValuation: false,
        })
            .then((raw) => {
                if (seq !== requestSeqRef.current) return;
                const result = unwrap(raw);
                if (!result?.data) return;

                setChartData(
                    result.data.map((d) => ({ date: d.date, close: d.close }))
                );
                setMeta(result);

                // If backend already included valuation for free (archive indices),
                // store it immediately and skip the second fetch entirely.
                if (result.valuationIncluded && Array.isArray(result.valuationData) && result.valuationData.length > 0) {
                    const valData = result.valuationData;
                    setValuationData(valData);
                    const cacheKey = `${index.symbol}:${period}`;
                    valuationCacheRef.current[cacheKey] = {
                        valuationData: valData,
                        meta: {
                            pe: result.pe, eps: result.eps, pb: result.pb,
                            valuationSeriesMeta: result.valuationSeriesMeta,
                        },
                    };
                }
            })
            .catch((err) => {
                if (seq !== requestSeqRef.current) return;
                if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
                console.error('Index detail fetch failed:', err);
                setChartData([]);
            })
            .finally(() => {
                if (seq === requestSeqRef.current) setLoading(false);
            });

        return () => controller.abort();
    }, [index?.symbol, index?.name, period]);

    // Secondary fetch: valuation data only when needed AND not already available.
    useEffect(() => {
        if (!index?.symbol || selectedMetric === 'close') return;

        // Already have valuation from primary fetch or cache?
        const cacheKey = `${index.symbol}:${period}`;
        const cached = valuationCacheRef.current[cacheKey];
        if (cached) {
            setValuationData(cached.valuationData);
            if (cached.meta) {
                setMeta((prev) => prev ? { ...prev, ...cached.meta } : prev);
            }
            return;
        }

        const controller = new AbortController();
        setValuationLoading(true);

        getIndexDetail(index.symbol, period, index.name, {
            signal: controller.signal,
            includeValuation: true,
        })
            .then((raw) => {
                const result = unwrap(raw);
                if (!result) return;
                const valData = Array.isArray(result.valuationData) ? result.valuationData : [];
                setValuationData(valData);
                valuationCacheRef.current[cacheKey] = {
                    valuationData: valData,
                    meta: {
                        pe: result.pe, eps: result.eps, pb: result.pb,
                        valuationSeriesMeta: result.valuationSeriesMeta,
                    },
                };
                setMeta((prev) => prev ? {
                    ...prev,
                    pe: result.pe, eps: result.eps, pb: result.pb,
                    valuationSeriesMeta: result.valuationSeriesMeta,
                } : prev);
            })
            .catch((err) => {
                if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
                console.error('Valuation fetch failed:', err);
            })
            .finally(() => setValuationLoading(false));

        return () => controller.abort();
    }, [index?.symbol, index?.name, period, selectedMetric]);

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        if (['1D', '5D'].includes(period)) {
            return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
        }
        return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) onClose();
    };

    // Compute whether actual data range is much shorter than selected period.
    // Fall back to deriving range from chartData if backend field is missing (old cache).
    const dataRangeNotice = React.useMemo(() => {
        const periodExpectedMonths = { '1M': 1, '3M': 3, '6M': 6, '1Y': 12, '3Y': 36, '5Y': 60 };
        const expected = periodExpectedMonths[period];
        if (!expected) return null;

        let fromStr, toStr;
        if (meta?.dataDateRange?.from && meta?.dataDateRange?.to) {
            fromStr = meta.dataDateRange.from;
            toStr = meta.dataDateRange.to;
        } else if (chartData.length >= 2) {
            // Derive from actual chart data
            fromStr = chartData[0]?.date?.slice(0, 10);
            toStr = chartData[chartData.length - 1]?.date?.slice(0, 10);
        } else {
            return null;
        }
        if (!fromStr || !toStr) return null;

        const from = new Date(fromStr);
        const to = new Date(toStr);
        const actualMonths = (to - from) / (1000 * 60 * 60 * 24 * 30.44);
        if (actualMonths < expected * 0.75) {
            const fromDisplay = from.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
            return `This index has data only from ${fromDisplay} (~${Math.round(actualMonths)} months available vs ${period} selected).`;
        }
        return null;
    }, [meta?.dataDateRange, chartData, period]);

    // O(n) merge using Map instead of O(n²) find()
    const mergedSeries = React.useMemo(() => {
        if (!valuationData.length) return chartData;
        const valMap = new Map(valuationData.map((v) => [v.date, v]));
        return chartData.map((point) => {
            const v = valMap.get(point.date) || {};
            return { ...point, pe: v.pe, eps: v.eps, pb: v.pb };
        });
    }, [chartData, valuationData]);

    const activeData = mergedSeries;
    const metricValues = activeData
        .map((item) => Number(item[selectedMetric]))
        .filter((value) => Number.isFinite(value));
    const metricAvg = metricValues.length
        ? metricValues.reduce((sum, value) => sum + value, 0) / metricValues.length
        : null;
    const metricMedian = metricValues.length
        ? (() => {
            const sorted = [...metricValues].sort((a, b) => a - b);
            const mid = Math.floor(sorted.length / 2);
            return sorted.length % 2 === 0
                ? (sorted[mid - 1] + sorted[mid]) / 2
                : sorted[mid];
        })()
        : null;
    const showReferenceLines = selectedMetric !== 'close' && metricValues.length > 0;

    const activeOption = CHART_OPTIONS.find((option) => option.key === selectedMetric) || CHART_OPTIONS[0];

    return (
        <div className="index-detail-overlay" onClick={handleOverlayClick}>
            <div className="index-detail-panel" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="index-detail-header">
                    <div>
                        <h2 className="index-detail-title">{index.name}</h2>
                        <span className="index-card-symbol" style={{ fontSize: '0.85rem' }}>
                            {index.symbol}
                        </span>
                    </div>
                    <button className="index-detail-close" onClick={onClose} aria-label="Close">
                        ✕
                    </button>
                </div>

                {/* Period selectors */}
                <div className="index-period-selectors">
                    {PERIODS.map((p) => (
                        <button
                            key={p}
                            className={`index-period-btn ${period === p ? 'index-period-btn--active' : ''}`}
                            onClick={() => setPeriod(p)}
                        >
                            {p}
                        </button>
                    ))}
                </div>

                <div className="index-period-selectors" style={{ marginTop: '-0.4rem' }}>
                    {CHART_OPTIONS.map((option) => (
                        <button
                            key={option.key}
                            className={`index-period-btn ${selectedMetric === option.key ? 'index-period-btn--active' : ''}`}
                            onClick={() => setSelectedMetric(option.key)}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>

                {/* Chart */}
                <div className="index-chart-container">
                    {loading ? (
                        <div className="funds-loading" style={{ paddingTop: '8rem' }}>
                            Loading chart data…
                        </div>
                    ) : activeData.length === 0 ? (
                        <div className="funds-empty">
                            <p>No chart data available for this period.</p>
                        </div>
                    ) : selectedMetric !== 'close' && valuationLoading ? (
                        <div className="funds-loading" style={{ paddingTop: '8rem' }}>
                            Loading valuation data…
                        </div>
                    ) : selectedMetric !== 'close' && metricValues.length === 0 ? (
                        <div className="funds-empty">
                            <p>{activeOption.label} data is not available for this index currently.</p>
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={activeData}>
                                <defs>
                                    <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="var(--brand)" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="var(--brand)" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid
                                    strokeDasharray="3 3"
                                    stroke="var(--border)"
                                    vertical={false}
                                />
                                <XAxis
                                    dataKey="date"
                                    tickFormatter={formatDate}
                                    tick={{ fontSize: 11, fill: 'var(--text-2)' }}
                                    axisLine={{ stroke: 'var(--border)' }}
                                    tickLine={false}
                                    minTickGap={40}
                                />
                                <YAxis
                                    domain={['auto', 'auto']}
                                    tick={{ fontSize: 11, fill: 'var(--text-2)' }}
                                    axisLine={false}
                                    tickLine={false}
                                    tickFormatter={(v) => Number(v)?.toLocaleString('en-IN')}
                                    width={65}
                                />
                                <Tooltip
                                    contentStyle={{
                                        background: 'var(--bg-2)',
                                        border: '1px solid var(--border)',
                                        borderRadius: 8,
                                        color: 'var(--text-1)',
                                        fontSize: '0.82rem',
                                    }}
                                    labelFormatter={formatDate}
                                    formatter={(val) => [Number(val)?.toLocaleString('en-IN'), activeOption.label]}
                                />
                                {showReferenceLines && metricAvg != null && (
                                    <ReferenceLine
                                        y={metricAvg}
                                        stroke="var(--warning)"
                                        strokeDasharray="4 4"
                                        label={{ value: `Avg ${formatMetric(metricAvg)}`, fill: 'var(--warning)', fontSize: 11 }}
                                    />
                                )}
                                {showReferenceLines && metricMedian != null && (
                                    <ReferenceLine
                                        y={metricMedian}
                                        stroke="var(--brand-2)"
                                        strokeDasharray="4 4"
                                        label={{ value: `Median ${formatMetric(metricMedian)}`, fill: 'var(--brand-2)', fontSize: 11 }}
                                    />
                                )}
                                <Area
                                    type="monotone"
                                    dataKey={selectedMetric}
                                    stroke="var(--brand)"
                                    strokeWidth={2}
                                    fillOpacity={1}
                                    fill="url(#chartGrad)"
                                    dot={false}
                                    activeDot={{ r: 4 }}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    )}
                </div>

                {/* Data availability notice */}
                {dataRangeNotice && (
                    <div style={{
                        fontSize: '0.75rem',
                        color: 'var(--warning)',
                        marginTop: '0.4rem',
                        padding: '0.3rem 0.6rem',
                        background: 'rgba(255,193,7,0.08)',
                        borderRadius: 6,
                        display: 'inline-block',
                    }}>
                        ⓘ {dataRangeNotice}
                    </div>
                )}

                {/* Summary */}
                {meta && (
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-2)', marginTop: '0.5rem' }}>
                        Last: <strong style={{ color: 'var(--text-1)' }}>{meta.last?.toLocaleString('en-IN')}</strong>
                        {' | '}Change: <strong style={{ color: (meta.change ?? 0) >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                            {(meta.change ?? 0) >= 0 ? '+' : ''}{meta.change} ({(meta.changePercent ?? 0) >= 0 ? '+' : ''}{meta.changePercent}%)
                        </strong>
                        {' | '}PE: <strong style={{ color: 'var(--text-1)' }}>{formatMetric(meta.pe)}</strong>
                        {' | '}EPS: <strong style={{ color: 'var(--text-1)' }}>{formatMetric(meta.eps)}</strong>
                        {' | '}P/B: <strong style={{ color: 'var(--text-1)' }}>{formatMetric(meta.pb)}</strong>
                        {showReferenceLines && metricAvg != null && (
                            <> {' | '}Avg: <strong style={{ color: 'var(--warning)' }}>{formatMetric(metricAvg)}</strong></>
                        )}
                        {showReferenceLines && metricMedian != null && (
                            <> {' | '}Median: <strong style={{ color: 'var(--brand-2)' }}>{formatMetric(metricMedian)}</strong></>
                        )}
                        {meta.lastUpdated && <> | Updated: {new Date(meta.lastUpdated).toLocaleString()}</>}
                    </div>
                )}
                {selectedMetric !== 'close' && meta?.valuationSeriesMeta?.source && (
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-2)', marginTop: '0.4rem', opacity: 0.85 }}>
                        {meta.valuationSeriesMeta.source === 'nse_archive_daily' || meta.valuationSeriesMeta.source === 'nse_archive_sampled'
                            ? 'Valuation series source: NSE historical daily index files (with forward fill on non-sampled days).'
                            : 'Valuation series is period-aligned from latest PE/PB snapshot and current close history.'}
                    </div>
                )}
            </div>
        </div>
    );
};

export default IndexDetailModal;
